import React from "react";
import { observer } from "mobx-react";
import { OrbitComponent } from "../../base-components/orbit-component/orbit-component";
import { action, observable } from "mobx";
import { AnimationDismissType, FADE_OUT_DURATION_IN_SECONDS, FLOATING_BUTTON_DURATION_IN_SECONDS, NATIVE_ANIMATION_FRAME_WIDTH } from "./burnout-animation-constants";
import { BurnoutAnimationContainer } from "./boxes/burnout-animation-container.box";
import { BurnoutAnimationBox } from "./boxes/burnout-animation.box";
import { CongratulationsTextBox } from "./boxes/burnout-animation-text.box";
import { TrackEntry } from "../../../services/pixi-app/pixi-app.service.interface";
import { imagePreload } from "../../../../common/utils/image-preload";

const ExitAnimationDurationByDismissType = {
    [AnimationDismissType.FadeOut]: FADE_OUT_DURATION_IN_SECONDS,
    [AnimationDismissType.FloatingButton]: FLOATING_BUTTON_DURATION_IN_SECONDS
};

interface IBurnoutAnimationProps {
    zIndex: number;
    getExitAnimation(): AnimationDismissType;
    onExitAnimationEnd(exitAnimationType: AnimationDismissType): void;
    paused?: boolean;
}

@observer
export class BurnoutAnimation extends OrbitComponent<IBurnoutAnimationProps> {
    /* During the load of the assets I don't want to accidentally start another load and start the animation */
    private starting: boolean = false;
    /* Sometimes the last frame iamge is loaded again at the end of the animation.
    My supposition is that when the preloaded image is not used immediately the browser assumes it will not be used so it unloads it.
    So I keep the reference to the image tag so the garbage collecter will not dispose the image.
    Apparently it works */
    private lastFrameCache: HTMLImageElement | null = null;

    constructor(props: IBurnoutAnimationProps) {
        super(props);
        this._viewportHeight = window.innerHeight;
    }
    componentDidMount(): void {
        if (!this.props.paused) {
            this.startAnimation();
        }
    }

    componentDidUpdate(prevProps: IBurnoutAnimationProps) {
        if (this.props.paused !== prevProps.paused) {
            if (!this.props.paused && !this.starting) {
                this.startAnimation();
            } else {
                if (this._onPauseHandler) {
                    this._onPauseHandler(!!this.props.paused);
                }
            }
        }
    }

    componentWillUnmount() {
        this.lastFrameCache = null;
    }

    @observable
    private _dismissType = AnimationDismissType.FadeOut;
    @observable
    private _hasAnimationEnded = false;
    @observable
    private _showCongratsText = false;
    private _animationElement: HTMLDivElement | null = null;
    private _animationWidth = NATIVE_ANIMATION_FRAME_WIDTH;
    /* In mobile, i cannot use 100vh in css because it does not consider the browser chrome (tool bar and address bars and such).
    So I have to do this horrible hack, I take the height from JS */
    private _viewportHeight = NATIVE_ANIMATION_FRAME_WIDTH;

    private _onPauseHandler: ((pause: boolean) => void) | null = null;

    private async startAnimation() {
        /* If it is already loading, then leave it be */
        if (this.starting) {
            return;
        }
        this.starting = true;

        let {app: pixiApp, dispose: disposePixiApp, PixiSpine} = await this.services.pixiApp.createPixiApp({
            width: 600, height: 1000, force2DCanvasContext: true
        });

        pixiApp.loader.add('burnoutAnimationResource', this.services.theme.currentTheme.images.burnoutAnimation.pixiJson.toString()).load(async (loader, resources) => {
            /* This happens for example if we use "@pixi-spine/runtime-3.8" library instead of "pixi-spine" or "@pixi-spine/all-3.8" */
            /* Also it happens if the json is not well formatted according to specs (RFC-8259, ECMA-404) (https://www.json.org/json-en.html , https://en.wikipedia.org/wiki/JSON).
            For example, all members have to be quoted, or else is not a JSON but an object. Ex: NOT OK: {key: "value"} / OK: {"key": "value"}
            I like to use this validator+formatter: https://elmah.io/tools/json-formatter/ */
            /* Otherwise, this shouldn't happen normally, but since the member is optional in typings, better safe than sorry */
            if (!resources.burnoutAnimationResource.spineData) {
                disposePixiApp();
                return;
            }

            /* Animation assets are all loaded and we can play, but before that we should also preload the last frame image to avoid flicker at the end of animation */
            imagePreload(this.services.theme.currentTheme.images.burnoutAnimation.lastFrame.toString()).then((response) => {
                this.lastFrameCache = response.imageObj;
            });

            let spineAnimation = new PixiSpine.Spine(resources.burnoutAnimationResource.spineData);
            let currentAnimationTrackEntry: TrackEntry | null;

            this._onPauseHandler = (pause: boolean) => {
                // spineAnimation.autoUpdate = !pause;
                if (currentAnimationTrackEntry) {
                    currentAnimationTrackEntry.timeScale = pause ? 0 : 1;
                }
            }

            // play animation
            const runAnimation = () => {
                spineAnimation.state.setAnimation(0, "1-zoom", false);
                spineAnimation.state.addAnimation(0, "2-static_close", false, 0.3);
                const spineAnimationTrackEntry3 = spineAnimation.state.addAnimation(0, "3-opening", false, 0);
                spineAnimationTrackEntry3.listener = {
                    event: (entry, event) => {
                        console.log('BURNOUT TRACK 3 event', event);
                        if (event.stringValue === "open_text") {
                            this.setShowCongratsText(true);
                        }
                    }
                };
                spineAnimation.state.addAnimation(0, "4-static _open", false, 0);
                spineAnimation.state.addAnimation(0, "5-static _open_tranzition", false, 1.5);
                const spineAnimationTrackEntry6 = spineAnimation.state.addAnimation(0, "6-static _open_empty", false, 0);
                spineAnimationTrackEntry6.listener = {
                    start: (entry) => {
                        this.setShowCongratsText(false);
                    }
                };
                spineAnimation.state.addAnimation(0, "7-closing", false, 0);
                const finalTrackEntry = spineAnimation.state.addAnimation(0, "8-closed", false, 0);
                finalTrackEntry.listener = {
                    complete: (entry) => {
                        this.setDismissType(this.props.getExitAnimation());
                        this.markAnimationEnding();

                        setTimeout(() => {
                            this._onPauseHandler = null;
                            spineAnimation.autoUpdate = false;
                            spineAnimation.destroy();
                            spineAnimation = undefined;
                            disposePixiApp();
                            this.starting = false;
                        });

                        setTimeout(() => {
                            this.lastFrameCache = null;
                            this.props.onExitAnimationEnd(this._dismissType);
                        }, ExitAnimationDurationByDismissType[this._dismissType] * 1000 + 100);
                    }
                };
            }

            spineAnimation.state.addListener({
                start: (entry) => {
                    currentAnimationTrackEntry = entry;
                }
            });

            runAnimation();

            pixiApp.view.style.position = "absolute";
            pixiApp.view.style.top = "0";
            pixiApp.view.style.width = "100%";
            this._viewportHeight = this.services.window.innerHeight;
            // add the animation to the scene and render...
            this._animationElement.appendChild(pixiApp.view);
            pixiApp.stage.addChild(spineAnimation);
        });

    }

    @action
    private markAnimationEnding() {
        this._hasAnimationEnded = true;
    };

    @action
    private setDismissType(v: AnimationDismissType) {
        this._dismissType = v;
    }

    @action
    private setShowCongratsText(v: boolean) {
        this._showCongratsText = v;
    }

    private captureAnimationDivElement = (el: HTMLDivElement | null) => {
        this._animationElement = el;
        if (el) {
            this._animationWidth = el.getBoundingClientRect().width;
        }
    }

    render() {
        /* Delay the rendering of the animation for the time the image is preloaded.
        As soon we can start the animation then we can display, measure the size of the animation based on viewport and play */
        return <BurnoutAnimationContainer className={'cy-burnout-animation-container'}
            animationEnded={this._hasAnimationEnded}
            dismissType={this._dismissType}
            viewportHeight={this._viewportHeight}
            zIndex={this.props.zIndex}
        >
            <BurnoutAnimationBox
                ref={this.captureAnimationDivElement}
                animationEnded={this._hasAnimationEnded}
                dismissType={this._dismissType}
            >
                <CongratulationsTextBox
                    animationWidth={this._animationWidth}
                    show={this._showCongratsText}
                >
                    {this.services.translation.translate("BURNOUT_MESSAGE_LINE_1")}
                    <br />
                    {this.services.translation.translate("BURNOUT_MESSAGE_LINE_2")}
                </CongratulationsTextBox>
            </BurnoutAnimationBox>
        </BurnoutAnimationContainer>;
    }
}
